/**
 * The ONE way a storage browser tab opens (tree double-click / Open, pinned
 * row, MCP `ui_open_tab('storage-explorer')`). Dedupes by connection + root
 * (`storageResourceKey`), places the cold-open perf mark immediately before
 * `addTab` (the tab id is generated inside `addTab`, so the correlation key is
 * the opener-supplied `openToken`, spec §6.3) and fires the browse-open event
 * with enum properties only.
 */
import { toast } from '../../../../components/ui/toaster';
import { getConnectionById } from '../../../connections/utils/connectionLookup';
import { storageResourceKey, type StorageResourceRef } from '../../../../types/azureStorage';
import { useWorkspaceStore } from '../../core/store/workspaceStore';
import {
  createDefaultStorageExplorerView,
  isStorageExplorerTab,
  storageExplorerModeFor,
  type StorageExplorerView,
  type StorageExplorerEntityView,
} from '../../core/types/workspaceTypes';
import { trackStorageBrowseOpen, type StorageBrowseOpenSource } from '../services/storageAnalytics';
import { getStorageAccountInfoSnapshot } from '../../../database-objects/store/storageAccountInfoStore';
import { mapLocationIntoRoot, resolveStorageViewRoot, type StorageViewIntent } from '../hns/hnsView';
import { storageViewTabTitle } from './storageViewTabTitle';
import { safeConnectionAlias, safeConnectionServer } from '../../../../types/safeConnectionDisplay';

export type OpenStorageExplorerTabArgs = {
  readonly connectionId: string;
  /** container | table | queue | share | hnsPath directory | blob (single-blob attachment). */
  readonly root: StorageResourceRef;
  /** Initial location under the root; defaults to the root. */
  readonly resource?: StorageResourceRef;
  readonly source: Exclude<StorageBrowseOpenSource, 'restore'>;
  readonly view?: Partial<StorageExplorerView>;
  readonly entityView?: StorageExplorerEntityView;
  /** S8 (D1 / S8-09): `default` routes an HNS account's containers to the Data Lake view; the view switch passes an explicit intent the default routing never overrides. */
  readonly viewIntent?: StorageViewIntent;
};

export function newStorageOpenToken(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export function storageOpenMarkName(openToken: string): string {
  return `storage-open:${openToken}`;
}

export function storageFirstRowsMarkName(openToken: string): string {
  return `storage-first-rows:${openToken}`;
}

/** Returns the tab id (existing or new), or `null` when the connection is unknown. */
export function openStorageExplorerTab(args: OpenStorageExplorerTabArgs): string | null {
  const { connectionId, source } = args;
  const store = useWorkspaceStore.getState();
  // S8: the EFFECTIVE root is resolved before dedupe, so a repeated tree open finds the Data Lake tab (finding S8-09).
  const root = resolveStorageViewRoot(args.root, args.viewIntent ?? 'default', getStorageAccountInfoSnapshot(connectionId)?.hns ?? 'unknown');
  const explicitResource = args.resource ? mapLocationIntoRoot(root, args.resource) : undefined;
  const rootKey = storageResourceKey(root);
  const existing = store.tabs.find(
    (tab) => isStorageExplorerTab(tab) && tab.content.connectionId === connectionId && storageResourceKey(tab.content.root) === rootKey,
  );
  if (existing) {
    if (args.entityView) {
      store.updateTabContent<'storage-explorer'>(existing.id, { entityView: args.entityView });
    }
    // An explicit location (the view switch always passes one) moves the existing tab there; a plain re-open keeps it where the user left it.
    if (explicitResource && isStorageExplorerTab(existing) && storageResourceKey(existing.content.resource) !== storageResourceKey(explicitResource)) {
      store.updateTabContent<'storage-explorer'>(existing.id, { resource: explicitResource, view: { ...existing.content.view, prefixFilter: '' } });
      store.updateTabTitle(existing.id, storageViewTabTitle(connectionId, explicitResource));
    }
    store.setActiveTab(existing.id);
    return existing.id;
  }

  const { connection } = getConnectionById(connectionId);
  if (!connection) {
    toast.error({ title: 'Error', description: 'Connection not found' });
    return null;
  }

  const resource = explicitResource ?? root;
  const mode = storageExplorerModeFor(root);
  const openToken = newStorageOpenToken();
  if (typeof performance !== 'undefined' && typeof performance.mark === 'function') {
    performance.mark(storageOpenMarkName(openToken));
  }
  const id = store.addTab({
    type: 'storage-explorer',
    title: storageViewTabTitle(connectionId, resource),
    connectionInfo: {
      connectionId,
      connectionName: safeConnectionAlias(connection, 'Unnamed Connection'),
      serverHost: safeConnectionServer(connection),
    },
    content: {
      connectionId,
      root,
      resource,
      mode,
      view: { ...createDefaultStorageExplorerView(), ...(args.view ?? {}) },
      entityView: args.entityView,
      openToken,
    },
  });
  trackStorageBrowseOpen(mode, source);
  return id;
}
